import React, { useLayoutEffect } from 'react';
import noop from 'lodash/noop';
import { token } from '@atlaskit/tokens';
import {
	UNSAFE_MAIN_BLOCK_START_FOR_LEGACY_PAGES_ONLY,
	UNSAFE_MAIN_INLINE_END_FOR_LEGACY_PAGES_ONLY,
	UNSAFE_MAIN_INLINE_START_FOR_LEGACY_PAGES_ONLY,
} from '@atlaskit/navigation-system/layout/main';
import {
	Tokens,
	BUSINESS_NAV_HEIGHT,
	NAV4_BUSINESS_NAV_HEIGHT,
} from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { Main } from '@atlassian/jira-navigation-system/src/index.tsx';
import { LEGACY_PAGE_INSET_BLOCK_START } from '@atlassian/jira-platform-ui-banners-jsm-packaging-updates/src/constants.tsx';
import { useBannerJsmPackagingUpdates } from '@atlassian/jira-platform-ui-banners-jsm-packaging-updates/src/controllers/index.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { useProjectTheme } from '@atlassian/jira-project-theme-page-container/src/controllers/project-theme/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { isSystemNavPath } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/system/index.tsx';
import { isIssuesNavPath } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/issues/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { isChrome } from '@atlassian/jira-common-util-browser/src/index.tsx';
import { PageContainer } from '../../common/ui/page-container/main.tsx';
import LegacyRouteComponent from './router/index.tsx';
import {
	LayoutMainWrapper,
	LegacyEntryStyleOverrides,
	LegacyPageRightSidebarStyles,
} from './styled.tsx';
import type { LegacyPageProps } from './types.tsx';

/**
 * This applies styles that are used as a hack to position legacy content rendered
 * from the monolith into where the content should be positioned if it was a child.
 * In this case it's literally not a child but a sibling. Do NOT add more styles here,
 * especially if it's for content inside the Jira SPA.
 *
 * @deprecated
 */
const LegacyPagePositioningHack = () => {
	const { hasTheming, hasProjectViewNavigation } = useProjectTheme();
	const { isEnabled: isJsmPackagingUpdatesEnabled } = useBannerJsmPackagingUpdates();

	const currentRoute = useRouterRoute();
	const jiraIssueTermBannerInset =
		isSystemNavPath(currentRoute.path) || isIssuesNavPath(currentRoute.path) ? '52px' : '0px';

	const jsmPackagingBannerInset = isJsmPackagingUpdatesEnabled
		? LEGACY_PAGE_INSET_BLOCK_START
		: '0px';

	const inlineBlockStart = hasProjectViewNavigation
		? `calc(${UNSAFE_MAIN_BLOCK_START_FOR_LEGACY_PAGES_ONLY} + ${getWillShowNav4() ? NAV4_BUSINESS_NAV_HEIGHT : BUSINESS_NAV_HEIGHT} + ${jsmPackagingBannerInset} + ${jiraIssueTermBannerInset} + 1px)`
		: `calc(${UNSAFE_MAIN_BLOCK_START_FOR_LEGACY_PAGES_ONLY} + ${jsmPackagingBannerInset} + ${jiraIssueTermBannerInset})`;
	const background = hasTheming ? Tokens.WRAPPER_BACKGROUND : token('elevation.surface');
	const padding = isVisualRefreshEnabled() ? '0 24px' : '0 40px';

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
		<style
			data-page-container-legacy-hack
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={{
				__html: `
#navigation-app {
	width: 100%;
	z-index: auto;
	order: initial;
}
#page-body > #content {
	position: absolute;
	inset-block-start: ${inlineBlockStart};
	inset-block-end: 0;
	inset-inline-start: ${UNSAFE_MAIN_INLINE_START_FOR_LEGACY_PAGES_ONLY};
	inset-inline-end: ${UNSAFE_MAIN_INLINE_END_FOR_LEGACY_PAGES_ONLY};
	overflow: auto;
	margin: 0;
	padding: ${padding};
	background-color: ${background};
}
/* start GIN/NIN/oldIssueView */
.page-issue-navigator #page-body > #content,
.ajax-issue-search-and-view  #page-body > div#content {
	height: initial !important;
}
/* end GIN/NIN/oldIssueView */
`,
			}}
		/>
	);
};

const useRouterRoute = createRouterSelector((state) => state.route);

/**
 * This overrides styles from legacy entrypoint pages, yet another type of legacy page.
 *
 * @deprecated
 */
const LegacyEntryPositioningHack = () => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
	<style
		// eslint-disable-next-line react/no-danger
		dangerouslySetInnerHTML={{
			__html: `
#page-body > #content {
	margin: 0;
}`,
		}}
	/>
);

/* IMPORTANT:
 * When making any changes to the OLD component, please ensure that you also make the same changes to the
 * NEW component. The feature flag used is part of the Navigation Refresh work and we need to ensure that
 * we do not introduce tech debt for the roll-out by only updating the current component that is due to be
 * removed.
 *
 * If you have any concerns or questions please reach out to the #jira-navigation channel. Thanks!
 */
const NewLegacyPage = ({
	id,
	locale,
	baseUrl,
	messages,
	Router,
	routerLinkComponent,
	children = null,
	onLoad = noop,
	pageType,
}: LegacyPageProps) => {
	useLayoutEffect(() => {
		onLoad();

		// This is a hack to fix Chrome specific rendering with legacy pages. There is a situation
		// where parts of the page are not rendered. This code forces a repaint of the page to ensure
		// that everything is visible to the user.
		if (isChrome()) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const el = document.getElementsByClassName('aui-page-panel');
			if (el.length > 0) {
				// @ts-expect-error - TS2339 - Property '0' does not exist on type 'HTMLCollectionOf<Element>'
				el[0].style.display = 'none';
				// @ts-expect-error - TS2339 - Property '0' does not exist on type 'HTMLCollectionOf<Element>'
				el[0].offsetHeight; // This looks weird, but is required to force a repaint
				// @ts-expect-error - TS2339 - Property '0' does not exist on type 'HTMLCollectionOf<Element>'
				el[0].style.display = 'block';
			}
		}
	}, [onLoad]);

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="navigationNext">
			{pageType === 'legacy-page' && <LegacyPagePositioningHack />}
			{pageType === 'legacy-entry' && <LegacyEntryPositioningHack />}
			<PageContainer
				id={id}
				baseUrl={baseUrl}
				locale={locale}
				messages={messages}
				Router={Router}
				routerLinkComponent={routerLinkComponent}
			>
				{/* @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'RouteContext': location, query, route, match, action */}
				<LegacyRouteComponent />
				<>{children}</>
			</PageContainer>
		</ContextualAnalyticsData>
	);
};

/**
 * Provides a themed and structured layout for displaying legacy pages or entries. This component
 * automatically applies specific styles and layout adjustments based on the type of content
 * being displayed, ensuring a consistent user experience. It also integrates analytics for
 * tracking page usage.
 */
const OldLegacyPage = ({
	id,
	locale,
	baseUrl,
	messages,
	Router,
	routerLinkComponent,
	children = null,
	onLoad = noop,
	pageType,
}: LegacyPageProps) => {
	useLayoutEffect(() => {
		onLoad();
	}, [onLoad]);
	return (
		<>
			{pageType === 'legacy-page' && <LegacyPageRightSidebarStyles />}
			{pageType === 'legacy-entry' && <LegacyEntryStyleOverrides />}
			<ContextualAnalyticsData sourceType={SCREEN} sourceName="navigationNext">
				<PageContainer
					id={id}
					baseUrl={baseUrl}
					locale={locale}
					messages={messages}
					Router={Router}
					routerLinkComponent={routerLinkComponent}
				>
					{/* @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'RouteContext': location, query, route, match, action */}
					<LegacyRouteComponent />
					<LayoutMainWrapper>
						<Main>{children}</Main>
					</LayoutMainWrapper>
				</PageContainer>
			</ContextualAnalyticsData>
		</>
	);
};

export default componentWithCondition(getWillShowNav4, NewLegacyPage, OldLegacyPage);
